(function () {
  'use strict';

  window.initOrderedItemsVanilla = function (root, context) {
    if (!root) return;

    let orderedItems = [];
    let searchTimeout;
    let sortField;
    let showHiddenItems = false;

    const template = document.getElementById('item-template')?.innerHTML || '';
    const cartUrl = context?.urls?.cart || '/cart';
    const customerId = context?.customer_id || root.dataset.customerId || 'guest';
    const customerEmail = context.customer_email;
    const itemHistoryModal = createHistoryModal();
    const hiddenItemsKey = `ordered-items:hidden:${customerId}`;

    const $ = (sel, scope = document) => scope.querySelector(sel);
    const $$ = (sel, scope = document) => [...scope.querySelectorAll(sel)];

    const cache = {
      timeout: 60 * 60 * 1000,

      get(url) {
        try {
          return JSON.parse(localStorage.getItem(url));
        } catch {
          return null;
        }
      },

      exists(url) {
        const item = this.get(url);
        return item && Date.now() - item._ < this.timeout;
      },

      set(url, data) {
        const cached = { _: Date.now(), data };
        localStorage.setItem(url, JSON.stringify(cached));
        return cached;
      }
    };

    function getCachedJson(url) {
      if (cache.exists(url)) {
        return Promise.resolve(cache.get(url));
      }

      return fetch(url)
        .then(res => {
          if (!res.ok) throw new Error(`Request failed ${res.status}`);
          return res.json();
        })
        .then(data => cache.set(url, data));
    }

    function formatPrice(price) {
      return '$' + Number(price || 0).toFixed(2);
    }

    function parseMoney(value) {
      return Number(String(value || '').replace(/[^0-9.-]/g, ''));
    }

    function getOrderTimestamp(order) {
      const timestamp = Number(order.created_at_timestamp);
      if (timestamp) return timestamp;

      const date = new Date(order.created_at_iso || order.created_at || order.processed_at || order.date);
      const time = date.getTime();
      return Number.isNaN(time) ? 0 : time;
    }

    function formatOrderDate(order) {
      const timestamp = getOrderTimestamp(order);
      if (!timestamp) return order.created_at || order.created_at_iso || '';

      return new Date(timestamp).toLocaleDateString('en-US');
    }

    function getHiddenItems() {
      try {
        const items = JSON.parse(localStorage.getItem(hiddenItemsKey) || '[]');
        return Array.isArray(items) ? new Set(items.map(String)) : new Set();
      } catch {
        return new Set();
      }
    }

    function setHiddenItems(items) {
      try {
        localStorage.setItem(hiddenItemsKey, JSON.stringify([...items]));
      } catch (err) {
        console.error(err);
      }
    }

    function setAddToCartLoading(isLoading) {
      let overlay = document.getElementById('ordered-items-add-to-cart-loading');

      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'ordered-items-add-to-cart-loading';
        overlay.className = 'ordered-items-add-to-cart-loading';
        overlay.innerHTML = '<div class="loadingOverlay"></div>';
        document.body.appendChild(overlay);
        injectAddToCartLoadingStyles();
      }

      overlay.classList.toggle('is-active', isLoading);
    }

    function injectAddToCartLoadingStyles() {
      if (document.getElementById('ordered-items-add-to-cart-loading-styles')) return;

      const style = document.createElement('style');
      style.id = 'ordered-items-add-to-cart-loading-styles';
      style.textContent = `
        #ordered-items-add-to-cart-loading {
          align-items: center;
          background: rgba(255, 255, 255, 0.72);
          bottom: 0;
          display: none;
          justify-content: center;
          left: 0;
          position: fixed;
          right: 0;
          top: 0;
          z-index: 2147483000;
        }

        #ordered-items-add-to-cart-loading.is-active {
          display: flex;
        }

        #ordered-items-add-to-cart-loading .loadingOverlay {
          display: block !important;
          height: 80px;
          position: relative;
          width: 80px;
        }
      `;
      document.head.appendChild(style);
    }

    function supplant(html, data) {
      return html.replace(/{([^{}]*)}/g, function (_, key) {
        const val = data[key];
        return typeof val === 'string' || typeof val === 'number' ? val : '';
      });
    }

    function maxByDate(items, field) {
      return items.reduce((max, item) => {
        return new Date(item[field]) > new Date(max[field]) ? item : max;
      }, items[0]);
    }

    function sortBy(items, field) {
      return items.slice().sort((a, b) => {
        const av = a[field];
        const bv = b[field];

        if (!isNaN(av) && !isNaN(bv)) return Number(av) - Number(bv);
        return String(av || '').localeCompare(String(bv || ''), undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      });
    }

    function getPrice(product, sku) {
      const variant = product.variants?.find(v => v.sku === sku);
      return variant ? variant.price : '';
    }

    function getVariantId(product, sku) {
      const variant = product.variants?.find(v => v.sku === sku);
      return variant ? variant.id : undefined;
    }

    function getBulk(product, sku) {
      if (product.variant_bulk_pricing_rules) {
        const variantRules = product.variant_bulk_pricing_rules.find(v => v.sku === sku);
        if (variantRules) {
          return variantRules.bulk_pricing_rules.filter(rule => rule.amount);
        }
      }

      if (product.bulk_pricing_rules?.length) {
        return product.bulk_pricing_rules.filter(rule => rule.amount);
      }

      return [];
    }

    function getVolumeDiscounts(line) {
      const discounts = [];

      (line.bulk_discount || []).forEach(rule => {
        const qty = Number(rule.qty);
        const price = parseMoney(rule.price);
        const variantPrice = parseMoney(rule.variant_price || line.price);

        if (qty && price && (!variantPrice || price < variantPrice)) {
          discounts.push({ qty, price });
        }
      });

      (line.tier_breaks || []).forEach(rule => {
        const qty = Number(rule.qty);
        const price = parseMoney(rule.price);

        if (qty && price) {
          discounts.push({ qty, price });
        }
      });

      return discounts
        .filter((rule, index, rules) => {
          return rules.findIndex(existing => existing.qty === rule.qty && existing.price === rule.price) === index;
        })
        .sort((a, b) => a.qty - b.qty);
    }

    function getBulkHtml(rules) {
      return rules.map(rule => {
        return `<div class="discount-row">${rule.qty}+ @ ${formatPrice(rule.price)}</div>`;
      }).join('');
    }

    function getSaleHtml(customFields) {
      const message = customFields.find(cf => cf.name === 'Sale_Message');
      return message
        ? `<div class="sale-row"><span>${message.value}</span></div>`
        : '';
    }

    function getItemOrderHistory(items) {
      return items.map(item => ({
        date: new Date(item.date_created).toLocaleDateString(),
        qty: item.quantity,
        price: formatPrice(item.price),
        id: item.order_id,
        phoneId: item.phone_id
      }));
    }

    function createHistoryModal() {
      if (typeof defaultModal === 'function') {
        return defaultModal();
      }

      let dialog = document.getElementById('ordered-items-history-modal');

      if (!dialog) {
        dialog = document.createElement('dialog');
        dialog.id = 'ordered-items-history-modal';
        dialog.className = 'ordered-items-history-modal';
        dialog.innerHTML = `
          <button type="button" class="ordered-items-history-modal__close" aria-label="Close">&times;</button>
          <div class="ordered-items-history-modal__content"></div>
        `;
        document.body.appendChild(dialog);
        injectHistoryModalStyles();

        dialog.addEventListener('click', e => {
          if (e.target === dialog || e.target.closest('.ordered-items-history-modal__close')) {
            if (typeof dialog.close === 'function') {
              dialog.close();
            } else {
              dialog.removeAttribute('open');
            }
          }
        });
      }

      return {
        open() {
          if (typeof dialog.showModal === 'function') {
            dialog.showModal();
          } else {
            dialog.setAttribute('open', '');
          }
        },
        updateContent(html) {
          const content = $('.ordered-items-history-modal__content', dialog);
          if (content) content.innerHTML = html;
        }
      };
    }

    function injectHistoryModalStyles() {
      if (document.getElementById('ordered-items-history-modal-styles')) return;

      const style = document.createElement('style');
      style.id = 'ordered-items-history-modal-styles';
      style.textContent = `
        #ordered-items-history-modal.ordered-items-history-modal {
          background: #f4f4f4;
          border: 1px solid #1f1f1f;
          padding: 0;
          width: min(810px, calc(100vw - 24px));
          max-height: min(720px, calc(100vh - 24px));
          overflow: auto;
          box-shadow: none;
        }

        .ordered-items-history-modal::backdrop {
          background: rgba(0, 0, 0, 0.55);
        }

        #ordered-items-history-modal .ordered-items-history-modal__close {
          position: absolute;
          top: 4px;
          right: 6px;
          border: 0;
          background: transparent;
          color: #000;
          cursor: pointer;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 42px;
          font-weight: 300;
          line-height: 1;
          padding: 0;
        }

        #ordered-items-history-modal .item-order-history {
          padding: 34px 37px 36px;
        }

        #ordered-items-history-modal .item-order-history h3 {
          margin: 0;
          padding: 0 40px 9px 0;
          font-size: 24px;
          line-height: 1.25;
          font-weight: 700;
        }

        #ordered-items-history-modal .item-order-history-header,
        #ordered-items-history-modal .item-order-history-list {
          display: grid !important;
          grid-template-columns: 17% 42% 25% 16%;
          border-top: 1px solid #d0d0d0;
          margin: 0;
          width: 100%;
        }

        #ordered-items-history-modal .item-order-history-header {
          padding: 4px 0;
          font-size: 14px;
          font-weight: 700;
          align-items: center;
        }

        #ordered-items-history-modal .item-order-history-list {
          min-height: 81px;
          padding: 17px 0;
          font-size: 14px;
          line-height: 1.55;
          align-items: flex-start;
        }

        #ordered-items-history-modal .item-order-history-list:last-child {
          border-bottom: 0;
        }

        #ordered-items-history-modal .history-cell {
          box-sizing: border-box;
          display: block;
          margin: 0;
          min-width: 0;
          padding: 0 13px;
        }

        #ordered-items-history-modal .history-cell--order,
        #ordered-items-history-modal .history-cell--date {
          white-space: nowrap;
        }

        #ordered-items-history-modal .history-cell--qty,
        #ordered-items-history-modal .history-cell--date {
          text-align: left;
        }

        #ordered-items-history-modal .history-cell--title div {
          max-width: 230px;
        }
      `;
      document.head.appendChild(style);
    }

    function displayVariants(items) {
      const list = $('.ordered-items-list', root);
      if (!list) return;

      list.innerHTML = '';

      items.forEach(item => {
        if (!item.visible && !showHiddenItems) return;

        const wrapper = document.createElement('div');
        wrapper.innerHTML = supplant(template, item).trim();

        const row = wrapper.firstElementChild;
        if (!row) return;

        if (!item.visible) {
          const btn = $('.btn-toggle', row);
          if (btn) {
            btn.innerHTML = 'Show to History';
            btn.title = 'Show Item';
          }
        }

        list.appendChild(row);
      });

      updateHiddenItemsButton();
    }

    function updateHiddenItemsButton() {
      const hiddenButton = $('.btn-toggle-hidden-items');
      if (hiddenButton) {
        hiddenButton.classList.toggle('hidden', !orderedItems.some(item => !item.visible));
      }
    }

    function loadItems() {
  fetch('/?section_id=order-history-json', {
    credentials: 'same-origin'
  })
    .then(function (res) {
      if (!res.ok) {
        throw new Error('Orders section failed: ' + res.status);
      }

      return res.text();
    })
    .then(function (html) {
      const container = document.createElement('div');
      container.innerHTML = html;

      const jsonText = container.textContent.trim();

      if (!jsonText) {
        throw new Error('Orders JSON is empty');
      }

      const orders = JSON.parse(jsonText);
      const grouped = {};

      orders.forEach(function (order) {
        (order.items || []).forEach(function (line) {
          if (!line.sku) return;

          const sku = String(line.sku);
          const orderTime = getOrderTimestamp(order);
          const orderDate = formatOrderDate(order);
          const volumeDiscounts = getVolumeDiscounts(line);

          if (!grouped[sku]) {
            grouped[sku] = {
              sku: sku,
              title:
                (line.product_title || line.title || '') +
                (line.variant_title && line.variant_title !== 'Default Title'
                  ? ' / ' + line.variant_title
                  : ''),
              image: line.image || '',
              brand: line.brand || line.vendor || '',
              qty: '',
              lastQty: line.quantity,
              history: 0,
              price: formatPrice(line.price),
              raw_price: line.price,
              original_price: line.price,
              lastOrderDate: orderDate,
              lastOrderDateTimeStamp: orderTime,
              link: line.product_url || '#',
              variant_id: line.variant_id,
              product_id: line.product_id,
              visible: true,
              bulk: volumeDiscounts,
              bulkHtml: getBulkHtml(volumeDiscounts),
              saleHtml: '',
              custom_fields: [],
              order_history: []
            };
          }

          grouped[sku].history += 1;

          grouped[sku].order_history.push({
            date: orderDate,
            qty: line.quantity,
            price: formatPrice(line.price),
            id: order.order_number || order.name,
            url: order.url,
            timestamp: orderTime
          });

          if (orderTime > Number(grouped[sku].lastOrderDateTimeStamp || 0)) {
            grouped[sku].lastQty = line.quantity;
            grouped[sku].lastOrderDate = orderDate;
            grouped[sku].lastOrderDateTimeStamp = orderTime;
            grouped[sku].price = formatPrice(line.price);
            grouped[sku].raw_price = line.price;
            grouped[sku].original_price = line.price;
          }
        });
      });

      const hiddenItems = getHiddenItems();

      orderedItems = Object.values(grouped).map(function (item) {
        item.visible = !hiddenItems.has(String(item.sku));

        item.order_history.sort(function (a, b) {
          return b.timestamp - a.timestamp;
        });

        return item;
      });

      if (orderedItems.length) {
        displayVariants(orderedItems);

        const hasItems = document.querySelector('.has-items');
        if (hasItems) hasItems.style.display = 'block';
      } else {
        const noItems = document.querySelector('.no-items');
        if (noItems) noItems.style.display = 'block';
      }

      const loading = document.querySelector('.loadingOverlay');
      if (loading) loading.style.display = 'none';
    })
    .catch(function (err) {
      console.error(err);

      const loading = document.querySelector('.loadingOverlay');
      if (loading) loading.style.display = 'none';

      const noItems = document.querySelector('.no-items');
      if (noItems) noItems.style.display = 'block';
    });
}

    function filterItems(input) {
      clearTimeout(searchTimeout);

      searchTimeout = setTimeout(() => {
        const value = input.value.trim();

        if (value.length < 2) {
          displayVariants(orderedItems);
          return;
        }

        const words = value.toLowerCase().split(/\s+/);

        const filtered = orderedItems.filter(item => {
          return words.every(word => {
            return (
              item.sku.toLowerCase() === word ||
              item.title.toLowerCase().includes(word)
            );
          });
        });

        displayVariants(filtered);
      }, 300);
    }

    function onQtyChange(target) {
      const wrapper = target.closest('[data-quantity-change]');
      const input = $('input', wrapper);
      const row = target.closest('.order-item');
      const sku = $('[name="sku"]', row)?.value;

      const item = orderedItems.find(i => i.sku === sku);
      if (!item) return;

      let qty = parseInt(input.value || '0', 10);

      if (target.dataset.action === 'inc') qty++;
      if (target.dataset.action === 'dec' && qty > 0) qty--;

      const bulkPrice = item.bulk
        .filter(bp => qty >= bp.qty)
        .sort((a, b) => b.qty - a.qty)[0];

      item.qty = qty;

      if (bulkPrice) {
        item.raw_price = bulkPrice.price;
        item.price = formatPrice(bulkPrice.price);
      } else {
        item.raw_price = item.original_price;
        item.price = formatPrice(item.original_price);
      }

      const temp = document.createElement('div');
      temp.innerHTML = supplant(template, item).trim();

      row.replaceWith(temp.firstElementChild);
    }

    function sortItems(header) {
      const previous = sortField;
      sortField = header.dataset.field;

      orderedItems =
        previous === sortField
          ? sortBy(orderedItems, sortField).reverse()
          : sortBy(orderedItems, sortField);

      $$('.order-item-headers > div').forEach(el =>
        el.classList.remove('sort-by-active')
      );

      header.classList.add('sort-by-active');

      displayVariants(orderedItems);
    }

    function toggleHidden(button) {
      showHiddenItems = !showHiddenItems;
      button.textContent = showHiddenItems ? 'Hide Hidden Items' : 'Show Hidden Items';
      displayVariants(orderedItems);
    }

    function toggleItem(button) {
      const row = button.closest('.order-item');
      const sku = String(button.dataset.item || '');

      const item = orderedItems.find(i => i.sku === sku);
      if (!item) return;

      const action = item.visible ? 'hide' : 'show';
      item.visible = !item.visible;

      const hiddenItems = getHiddenItems();

      if (action === 'hide') {
        hiddenItems.add(sku);
        row.remove();
      } else {
        hiddenItems.delete(sku);
        button.innerHTML = 'Hide from History';
        button.title = 'Hide Item';
      }

      setHiddenItems(hiddenItems);
      updateHiddenItemsButton();
    }

    function openHistory(target) {
      const row = target.closest('.order-item');
      const sku = $('[name="sku"]', row)?.value;
      const item = orderedItems.find(i => i.sku === sku);

      if (!item || !itemHistoryModal) return;

      const historyHtml = item.order_history.map(h => {
        const orderCell = h.phoneId
          ? h.phoneId
          : h.url
            ? `<a href="${h.url}">${h.id}</a>`
            : h.id;

        return `
          <div class="item-order-history-list">
            <div class="history-cell history-cell--order">${orderCell}</div>
            <div class="item-order-history-list-title history-cell history-cell--title">
              <div>${item.title}</div>
            </div>
            <div class="history-cell history-cell--qty">${h.qty}</div>
            <div class="history-cell history-cell--date">${h.date}</div>
          </div>
        `;
      }).join('');

      const html = `
        <div class="item-order-history">
          <h3>Order History For: ${sku}</h3>
          <div class="item-order-history-header">
            <div class="history-cell history-cell--order">Order</div>
            <div class="history-cell history-cell--title">Title</div>
            <div class="history-cell history-cell--qty">Qty</div>
            <div class="history-cell history-cell--date">Date</div>
          </div>
          ${historyHtml}
        </div>
      `;

      itemHistoryModal.open({ size: 'medium' });

      setTimeout(() => {
        itemHistoryModal.updateContent(html);
      }, 300);
    }

    async function submit(e) {
      e.preventDefault();

      const itemsToAdd = $$('.order-item', root)
        .map(row => {
          const qty = parseInt($('[name="qty[]"]', row)?.value || '0', 10);
          const sku = $('[name="sku"]', row)?.value;
          const variantId =
            $('[name="variant_id"]', row)?.value ||
            orderedItems.find(item => item.sku === sku)?.variant_id;

          return { quantity: qty, id: Number(variantId), sku };
        })
        .filter(item => item.quantity > 0 && item.id);

      if (!itemsToAdd.length) return;

      setAddToCartLoading(true);

      try {
        const cartItemsEl = document.querySelector('cart-items-component[data-section-id]');
        const cartItemsSectionId = cartItemsEl?.dataset.sectionId;
        const sectionList = ['cart-icon-component'];

        if (cartItemsSectionId) {
          sectionList.push(cartItemsSectionId);
        }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: itemsToAdd.map(item => ({
              id: item.id,
              quantity: item.quantity
            })),
            sections: sectionList.join(',')
          })
        });

        if (!res.ok) {
          throw new Error('Add to cart failed: ' + res.status);
        }

        const addResponse = await res.json();
        const cartRes = await fetch('/cart.js', {
          headers: {
            'Accept': 'application/json'
          }
        });
        const cart = await cartRes.json();
        const cartUpdateEvent = new Event('cart:update', { bubbles: true });

        cartUpdateEvent.detail = {
          resource: cart,
          sourceId: 'ordered-items',
          data: {
            source: 'ordered-items',
            itemCount: cart.item_count,
            sections: addResponse.sections || {}
          }
        };

        document.dispatchEvent(cartUpdateEvent);

        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

        const drawer = document.querySelector('cart-drawer-component');
        if (drawer) {
          if (typeof drawer.showDialog === 'function') {
            drawer.showDialog();
          } else if (typeof drawer.open === 'function') {
            drawer.open();
          } else {
            drawer.querySelector('dialog')?.showModal?.();
          }
        } else {
          window.location.href = cartUrl;
        }
      } catch (err) {
        console.error(err);
      } finally {
        setAddToCartLoading(false);
      }
    }

    function stickyHeader() {
      const ordered = $('.ordered-items');
      const header = $('.order-item-header-container');
      const stickyMenu = $('[data-stickymenu]');

      if (!ordered || !header) return;

      const headerHeight =
        stickyMenu && getComputedStyle(stickyMenu).position === 'fixed'
          ? stickyMenu.offsetHeight
          : 0;

      const top =
        ordered.getBoundingClientRect().top + window.scrollY - headerHeight;

      if (window.scrollY >= top) {
        header.classList.add('stick');
        header.style.top = headerHeight + 'px';
      } else {
        header.classList.remove('stick');
        header.style.top = '0';
      }
    }

    document.addEventListener('click', e => {
      const qtyBtn = e.target.closest('[data-quantity-change] button.btn-qty');
      if (qtyBtn) {
        e.preventDefault();
        onQtyChange(qtyBtn);
        return;
      }

      const submitBtn = e.target.closest('.form-action-addToCart-multiple');
      if (submitBtn) {
        submit(e);
        return;
      }

      const historyBtn = e.target.closest('.open-history');
      if (historyBtn) {
        openHistory(historyBtn);
        return;
      }

      const sortHeader = e.target.closest('.order-item-headers > div');
      if (sortHeader) {
        sortItems(sortHeader);
        return;
      }

      const toggleBtn = e.target.closest('.order-item .btn-toggle');
      if (toggleBtn) {
        e.preventDefault();
        toggleItem(toggleBtn);
        return;
      }

      const hiddenBtn = e.target.closest('.btn-toggle-hidden-items');
      if (hiddenBtn) {
        toggleHidden(hiddenBtn);
      }
    });

    document.addEventListener('keyup', e => {
      if (e.target.closest('[data-quantity-change] input')) {
        onQtyChange(e.target);
      }
    });

    document.addEventListener('input', e => {
      const search = e.target.closest('.order-item-actions .search input');
      if (search) filterItems(search);
    });

    window.addEventListener('scroll', stickyHeader);

    loadItems();
  };

  document.addEventListener('DOMContentLoaded', function () {
  const wrapper = document.querySelector('.ordered-items');

    if (!wrapper) {
        console.error('Ordered items wrapper not found');
        return;
    }

    initOrderedItemsVanilla(wrapper, {
        urls: {
        cart: '/cart'
        }
    });
    });
})();
